import { defineStore } from 'pinia'
import { collection, onSnapshot, query, where } from 'firebase/firestore'
import type { IWidgetInstance } from '@team-milva/lab-models/widget'
import { Display } from '@team-milva/lab-models/display'
import type { IDisplay } from '@team-milva/lab-models/display'
import { db } from '@/firebase/init'
import { managementApi } from '@/globals/typescript/utils/api/managementClient'
import { useAccountStore } from '@/stores/accountStore'

export { Display }

interface IUpdateDisplayParams {
  name: Record<string, string>
  password: string
  widgets: IWidgetInstance[]
}

export const useDisplayStore = defineStore('display', {
  state: () => ({
    allDisplays: [] as Display[],
    unsubscribeAllDisplays: null as (() => void) | null,
  }),

  getters: {
    allDisplaysLoaded(): boolean {
      return !!this.unsubscribeAllDisplays
    },
  },

  actions: {
    getDisplays(): Promise<Display[]> {
      if (this.allDisplaysLoaded) {
        return Promise.resolve(this.allDisplays)
      }

      return new Promise((resolve) => {
        let resolved = false

        const accountStore = useAccountStore()

        const q = query(
          collection(db, 'Apps', '-lab-management-', 'Displays'),
          where('labAccountID', '==', accountStore.labAccount?.id)
        )

        const unsubscribe = onSnapshot(q, (querySnapshot) => {
          this.allDisplays = querySnapshot.docs
            .map((doc) => new Display(doc.data() as IDisplay))
            .sort((a, b) => a.timestamps.createdAt - b.timestamps.createdAt)

          this.unsubscribeAllDisplays = unsubscribe

          if (!resolved) {
            resolved = true
            resolve(this.allDisplays)
          }
        })
      })
    },

    async createDisplay(name: Record<string, string>): Promise<string> {
      const accountStore = useAccountStore()
      const labAccountID = accountStore.labAccount?.id ?? ''

      // TODO: remove cast after deploying and re-running openapi:gen — the
      // /displays endpoints aren't in the generated codegen types yet.
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const result = await (managementApi as any)(
        'post',
        'lab-accounts/{labAccountID}/displays',
        {
          path: { labAccountID },
          body: { name },
        }
      )
      if (!result.success) throw new Error(result.code)
      return result.data.id
    },

    // Fetches the decrypted password for the editor — never cache this anywhere
    // beyond the editor's own local form state.
    async getDisplayForEdit(displayID: string): Promise<{
      id: string
      name: Record<string, string>
      password: string
      widgets: IWidgetInstance[]
    }> {
      const accountStore = useAccountStore()
      const labAccountID = accountStore.labAccount?.id ?? ''

      // TODO: remove cast after deploying and re-running openapi:gen
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const result = await (managementApi as any)(
        'get',
        'lab-accounts/{labAccountID}/displays/{displayID}',
        { path: { labAccountID, displayID } }
      )
      if (!result.success) throw new Error(result.code)
      return result.data
    },

    async updateDisplay(
      displayID: string,
      params: IUpdateDisplayParams
    ): Promise<void> {
      const accountStore = useAccountStore()
      const labAccountID = accountStore.labAccount?.id ?? ''

      // TODO: remove cast after deploying and re-running openapi:gen
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const result = await (managementApi as any)(
        'patch',
        'lab-accounts/{labAccountID}/displays/{displayID}',
        {
          path: { labAccountID, displayID },
          body: params,
        }
      )
      if (!result.success) throw new Error(result.code)
    },

    async deleteDisplay(displayID: string): Promise<void> {
      const accountStore = useAccountStore()
      const labAccountID = accountStore.labAccount?.id ?? ''

      // TODO: remove cast after deploying and re-running openapi:gen
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const result = await (managementApi as any)(
        'delete',
        'lab-accounts/{labAccountID}/displays/{displayID}',
        { path: { labAccountID, displayID } }
      )
      if (!result.success) throw new Error(result.code)
    },

    resetStore(): void {
      this.allDisplays = []
      if (this.unsubscribeAllDisplays) {
        this.unsubscribeAllDisplays()
        this.unsubscribeAllDisplays = null
      }
    },
  },
})
